import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  FC,
  PropsWithChildren,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';

import { DefaultHeader } from '@formstack/phoenix/components/Typography';
import { Colors } from '@formstack/phoenix/components/colors/constants';

import { BaseLayoutElement } from '@/common/components/Layout';

import { FeatureFlagId, featureFlagDefinitions } from './config';
import { FeatureFlagsProvider } from './context';
import { FeatureFlagAvailability, FeatureFlagsContextType } from './types';

const FeatureFlagsControlPanelBase = styled(BaseLayoutElement)`
  flex: 0 0 auto;
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(100, 100, 100, 0.75);

  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;

  padding: 10em;

  z-index: 999998;

  overflow: auto;
`;
const FeatureFlagsContainer = styled.div`
  padding: 1em;
  background-color: ${Colors['phx-white']};
`;
const FeatureFlagsControlPanelTab = styled(BaseLayoutElement)`
  flex: 0 0 auto;
  position: absolute;
  top: 0;
  left: 50%;
  width: 10em;
  margin-left: -5em;
  padding: 0.5em;
  box-sizing: border-box;

  border-radius: 0 0 0.25em 0.25em;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;

  background-color: darkred;
  color: white;

  z-index: 999999;

  user-select: none;
  cursor: pointer;

  &:hover {
    background-color: red;
  }
`;
const FeatureFlagItemBase = styled(BaseLayoutElement)`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  background-color: white;

  padding: 0.5em;
`;
const FeatureFlagItemHeader = styled(BaseLayoutElement)`
  flex: 1 0 auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: stretch;
`;
const FeatureFlagItemTitle = styled(BaseLayoutElement)`
  font-weight: bold;
  font-size: 1.2em;
  padding-left: 0.5em;
`;
const FeatureFlagItemDescription = styled(BaseLayoutElement)`
  font-size: 1em;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  padding-left: 0.5em;
`;
const FeatureFlagItemAvailabilityNote = styled(FeatureFlagItemDescription)`
  font-style: italic;
  color: ${Colors['phx-gray-5']};
`;
const FeatureFlagInputLabel = styled.label`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  padding-left: 0.5em;
`;

type FeatureFlagItemProps = {
  featureFlagId: FeatureFlagId;
  featureFlagMeta: { name: string; description?: string };
  value: boolean;
  disabled?: boolean;
  onFeatureFlagToggle: (featureFlagId: FeatureFlagId) => void;
};

const isFeatureFlagId = (id: string): id is FeatureFlagId => id in featureFlagDefinitions;

const FeatureFlagItem: FC<FeatureFlagItemProps> = ({
  value,
  featureFlagId,
  featureFlagMeta: { name, description },
  disabled,
  onFeatureFlagToggle,
}) => {
  const onToggleEnabled = useCallback(
    () => onFeatureFlagToggle(featureFlagId),
    [onFeatureFlagToggle, featureFlagId]
  );

  return (
    <FeatureFlagItemBase>
      <FeatureFlagItemHeader>
        <FeatureFlagInputLabel>
          <input disabled={disabled} type="checkbox" checked={value} onChange={onToggleEnabled} />
          <FeatureFlagItemTitle title={featureFlagId}>{name}</FeatureFlagItemTitle>
        </FeatureFlagInputLabel>
        <FeatureFlagItemDescription
          title={description}
        >{`(${description})`}</FeatureFlagItemDescription>
        {disabled && (
          <FeatureFlagItemAvailabilityNote>
            Feature flag not available
          </FeatureFlagItemAvailabilityNote>
        )}
      </FeatureFlagItemHeader>
    </FeatureFlagItemBase>
  );
};

type FeatureFlagsProps = {
  ignoreStoredValues?: boolean;
};

const IS_PROD = process.env.COPILOT_BUILD_ENV === 'production';

/**
 * Use feature flags in your application.
 * Typically, used to wrap your entire app.
 * */
export const FeatureFlags: FC<PropsWithChildren<FeatureFlagsProps>> = ({ children }) => {
  const allLDFlags = useFlags();
  const featureFlags = useMemo(
    () =>
      Object.keys(featureFlagDefinitions).reduce<Partial<FeatureFlagAvailability>>(
        (prev, curr) => ({ ...prev, [curr]: allLDFlags[curr] }),
        {}
      ),
    [allLDFlags]
  );
  const [featureFlagAvailability, setFeatureFlagAvailability] =
    useState<Partial<FeatureFlagAvailability>>(featureFlags);

  // LaunchDarkly data synchronization
  useEffect(() => {
    setFeatureFlagAvailability(featureFlags);
  }, [featureFlags]);

  const toggleFeatureFlag = useCallback((featureFlagId: FeatureFlagId) => {
    setFeatureFlagAvailability((prev) => ({ ...prev, [featureFlagId]: !prev[featureFlagId] }));
  }, []);
  const featureFlagsContext: FeatureFlagsContextType = useMemo(
    () => ({
      featureFlagAvailability,
    }),
    [featureFlagAvailability]
  );
  const [open, setOpen] = useState<boolean>();
  const onToggleOpen = useCallback(() => {
    setOpen(!open);
  }, [open, setOpen]);
  const onBaseClick = useCallback(
    ({ currentTarget, target }: ReactMouseEvent<HTMLDivElement, MouseEvent>) => {
      if (target === currentTarget) {
        onToggleOpen();
      }
    },
    [onToggleOpen]
  );

  return (
    <FeatureFlagsProvider value={featureFlagsContext}>
      {!IS_PROD ? (
        <>
          <FeatureFlagsControlPanelTab id="FeatureFlagsControlPanelTab" onClick={onToggleOpen}>
            Feature Flags
          </FeatureFlagsControlPanelTab>
          <FeatureFlagsControlPanelBase
            id="FeatureFlagsControlPanelBase"
            onClick={onBaseClick}
            style={{
              display: open ? undefined : 'none',
            }}
          >
            <FeatureFlagsContainer>
              <DefaultHeader style={{ paddingTop: '20px' }}>
                Feature flags (changing the value will not cause it to be saved)
              </DefaultHeader>
              {Object.keys(featureFlagDefinitions)
                .filter(isFeatureFlagId)
                .map((id) => (
                  <FeatureFlagItem
                    key={id}
                    featureFlagId={id}
                    featureFlagMeta={{
                      name: id,
                      description: featureFlagDefinitions[id as FeatureFlagId].description,
                    }}
                    value={featureFlagAvailability[id] ?? false}
                    disabled={featureFlags?.[id] === undefined}
                    onFeatureFlagToggle={toggleFeatureFlag}
                  />
                ))}
            </FeatureFlagsContainer>
          </FeatureFlagsControlPanelBase>
        </>
      ) : undefined}
      {children}
    </FeatureFlagsProvider>
  );
};
